import { ArrowLeft, Calendar, Clock, Share2 } from 'lucide-react';
import { marked } from 'marked';
import { useEffect } from 'react';
import { updatePageMeta, addStructuredData, generateBlogPostStructuredData } from '../../utils/seo';

marked.setOptions({
  breaks: true,
  gfm: true,
  headerIds: true,
  mangle: false,
  sanitize: false,
});

const post = {
  "slug": "birthday-group-gift-coordination",
  "title": "Group Birthday Gifts: Coordinating the Perfect Surprise",
  "excerpt": "Organize memorable group birthday gifts without the chaos. Tips for collecting money, choosing gifts, and creating special moments.",
  "date": "2024-10-20",
  "readTime": "6 min read",
  "category": "Festivals",
  "image": "🎂",
  "heroImage": "https://images.unsplash.com/photo-1558636508-e0db3814bd1d?w=1200&h=600&fit=crop",
  "author": "SwapGifts Editorial Team",
  "content": "# Group Birthday Gifts: Coordinating the Perfect Surprise\n\nGroup birthday gifts let you give something more significant than individual presents. Here's how to coordinate smoothly.\n\n## When to Give Group Gifts\n\n**Perfect Occasions**:\n- Milestone birthdays (30th, 40th, 50th, etc.)\n- Close colleague or team member\n- Family member from all siblings\n- Close friend group\n- Expensive gift the person really wants\n\n## Step-by-Step Coordination\n\n**1. Designate a Coordinator** (3-4 weeks before):\n- One person manages everything\n- Collects money\n- Purchases gift\n- Organizes card/presentation\n\n**2. Set the Budget** (3 weeks before):\n- Suggest contribution per person ($10-30 typical)\n- Allow flexibility for different budgets\n- Be clear about deadline\n\n**3. Collect Contributions** (2-3 weeks before):\n- Use Venmo, PayPal, or Zelle\n- Cash works for small groups\n- Track who's paid\n- Send friendly reminders\n\n**4. Choose the Gift** (2 weeks before):\n- Consider wish list or hints\n- Get input from key contributors\n- Buy from returnable/exchangeable store\n- Save receipt\n\n**5. Presentation** (day of):\n- Group-signed card\n- All contributors present if possible\n- Include everyone's names\n- Plan surprise timing\n\n## Gift Ideas by Budget\n\n**$100-200 Total**:\n- Nice headphones or earbuds\n- Smartwatch or fitness tracker\n- Quality bag or wallet\n- Experience (concert tickets, dinner)\n- Subscription (annual)\n\n**$200-500 Total**:\n- Tablet or e-reader\n- High-end kitchen appliance\n- Weekend getaway\n- Designer item they want\n- Professional course or certification\n\n**$500+ Total**:\n- Laptop or tech upgrade\n- Jewelry piece\n- Travel experience\n- Major hobby equipment\n- Furniture piece\n\n## Office Birthday Gifts\n\n**Small Office (5-10 people)**:\n- $10-15 per person\n- Total: $50-150\n- Gift cards, office accessories, experience gifts\n\n**Large Office (20+ people)**:\n- $5-10 per person\n- Total: $100-200+\n- Significant gifts like tablets, luggage, or experiences\n\n**Boss's Birthday**:\n- Optional participation\n- Lower individual amount ($5)\n- Nice but not extravagant\n- Group meal or gift basket\n\n## Family Group Gifts\n\n**For Parents**:\n- Siblings pool together\n- Bigger ticket items they wouldn't buy themselves\n- Home improvements\n- Special trips or experiences\n\n**For Siblings**:\n- Set tradition of group gifts\n- Milestone birthdays\n- New parent/wedding gifts\n\n## Handling Challenges\n\n**Non-Contributors**:\n- Send gentle reminders\n- Set firm deadline\n- Proceed without late contributors\n- Only include names of those who contributed\n\n**Gift Selection Disagreements**:\n- Coordinator makes final decision\n- Or use quick poll with 2-3 options\n- Remember: it's about the recipient's preferences\n\n**Surprise Spoiling**:\n- Limit who knows details\n- Don't discuss in group chats person might see\n- Code words if needed\n- Plan presentation timing carefully\n\n## Creative Presentation Ideas\n\n- Video montage of well-wishes\n- Scavenger hunt to find gift\n- Large group signing poster\n- Photo book of memories plus gift card\n- \"This Is Your Life\" presentation\n\n## Digital Coordination Tools\n\n- SwapGifts for organizing\n- Group messaging apps\n- Payment apps (Venmo, Zelle)\n- Shared online wish lists\n- Survey tools for gift voting\n\n## Conclusion\n\nGroup gifts require coordination but create memorable moments. With clear communication and good organization, you'll give a present that's both meaningful and impressive."
};

export default function BirthdayGroupGiftCoordinationPage() {
  useEffect(() => {
    updatePageMeta({
      title: `${post.title} | SwapGifts Blog`,
      description: post.excerpt,
      ogTitle: post.title,
      ogDescription: post.excerpt,
      ogImage: post.heroImage || 'https://swapgifts.store/default-blog-image.jpg',
      canonicalUrl: `https://swapgifts.store/blog/${post.slug}`,
      keywords: `gift exchange, ${post.category.toLowerCase()}, secret santa, ${post.title.toLowerCase()}`
    });

    addStructuredData(generateBlogPostStructuredData({
      title: post.title,
      excerpt: post.excerpt,
      date: post.date,
      author: post.author || 'SwapGifts Editorial Team',
      heroImage: post.heroImage,
      slug: post.slug
    }));
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <a
          href="/blog"
          className="inline-flex items-center gap-2 text-green-600 font-medium hover:underline mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Blog
        </a>

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {post.heroImage && (
            <div className="w-full h-96 overflow-hidden">
              <img
                src={post.heroImage}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-8 md:p-12">
            <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                {post.category}
              </span>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(post.date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              {post.title}
            </h1>

            {post.author && (
              <p className="text-sm text-gray-500 mb-6">
                By <span className="font-medium text-gray-700">{post.author}</span>
              </p>
            )}

            <p className="text-xl text-gray-600 mb-8 leading-relaxed border-b border-gray-200 pb-8">
              {post.excerpt}
            </p>

            <div className="flex items-center gap-4 pb-8 mb-8 border-b border-gray-200">
              <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                <Share2 className="w-4 h-4" />
                Share
              </button>
            </div>

            <div
              className="prose prose-lg max-w-none
                prose-headings:font-bold prose-headings:text-gray-900 prose-headings:mt-8 prose-headings:mb-4
                prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6
                prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
                prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6
                prose-a:text-green-600 prose-a:no-underline hover:prose-a:underline
                prose-strong:text-gray-900 prose-strong:font-semibold
                prose-ul:my-6 prose-ul:space-y-2
                prose-ol:my-6 prose-ol:space-y-2
                prose-li:text-gray-700 prose-li:leading-relaxed
                prose-code:bg-gray-100 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm"
              dangerouslySetInnerHTML={{ __html: marked.parse(post.content) }}
            />
          </div>

          <div className="mt-12 bg-gradient-to-r from-green-600 to-blue-600 rounded-lg p-8 text-white text-center">
            <h2 className="text-2xl font-bold mb-4">Ready to organize your gift exchange?</h2>
            <p className="text-green-100 mb-6">
              Put these tips into action and create your exchange in minutes
            </p>
            <a
              href="/create"
              className="inline-block px-8 py-3 bg-white text-green-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              Start Drawing Names
            </a>
          </div>
        </div>
      </article>
    </div>
  );
}
